/**
 * Welcome Back After Cancellation Modal
 *
 * Trigger: Previously paying customer returns after reactivating a cancelled plan.
 * Shown once on first login after reactivation.
 *
 * Personalizes copy if cancellation reason was "too_expensive"
 * (stored in localStorage at cancel time).
 *
 * DEV: Set DEV_FORCE = true to show immediately without condition checks.
 */
import * as React from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useToast } from './ui/toast';
import {
  getSeatInfo,
  switchBillingTerm,
} from '../../application/routes/settings/components/PaymentMethods/paymentApi';

// TODO: Set to false before merging
const DEV_FORCE = false;

const MODAL_KEY_PREFIX = 'paiv_reactivation_modal_shown_';
const PREV_CANCEL_KEY_PREFIX = 'paiv_prev_cancel_at_period_end_';
export const CANCEL_REASON_KEY_PREFIX = 'paiv_last_cancel_reason_';

declare const global: { me: { id: string; orgID: string } };

export function WelcomeBackModal() {
  const toast = useToast();
  const orgId: string | undefined = (global as any)?.me?.orgID;

  const [open, setOpen] = useState(false);
  const [isPriceSensitive, setIsPriceSensitive] = useState(false);
  const [switching, setSwitching] = useState(false);

  const check = useCallback(async () => {
    if (!orgId) {
      return;
    }

    if (DEV_FORCE) {
      // Show price-sensitive variant in dev — toggle manually to test
      setIsPriceSensitive(false);
      setOpen(true);
      return;
    }

    const modalKey = `${MODAL_KEY_PREFIX}${orgId}`;
    if (localStorage.getItem(modalKey)) {
      return;
    }

    try {
      const res = await getSeatInfo(orgId);
      if (!res.success || !res.data) {
        return;
      }

      const { subscriptionStatus, cancelAtPeriodEnd, customerType } = res.data;

      if (customerType !== 'seat_based' || subscriptionStatus !== 'active') {
        localStorage.setItem(
          `${PREV_CANCEL_KEY_PREFIX}${orgId}`,
          String(cancelAtPeriodEnd)
        );
        return;
      }

      const prevCancelFlag = localStorage.getItem(
        `${PREV_CANCEL_KEY_PREFIX}${orgId}`
      );
      const justReactivated =
        prevCancelFlag === 'true' && cancelAtPeriodEnd === false;

      if (!justReactivated) {
        localStorage.setItem(
          `${PREV_CANCEL_KEY_PREFIX}${orgId}`,
          String(cancelAtPeriodEnd)
        );
        return;
      }

      const cancelReason = localStorage.getItem(
        `${CANCEL_REASON_KEY_PREFIX}${orgId}`
      );
      setIsPriceSensitive(cancelReason === 'too_expensive');
      setOpen(true);
    } catch {
      // Non-critical
    }
  }, [orgId]);

  useEffect(() => {
    const t = setTimeout(check, 1000);
    return () => clearTimeout(t);
  }, [check]);

  const handleSwitchToAnnual = async () => {
    if (!orgId) {
      return;
    }
    try {
      setSwitching(true);
      const res = await switchBillingTerm(orgId, 'yearly');
      if (res.success) {
        toast.success('Switched to annual billing. Welcome back!');
        markShown();
        setOpen(false);
      } else {
        toast.error(res.error || 'Failed to switch to annual');
      }
    } catch {
      toast.error('Unable to switch plans. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  const handleStayOnQuarterly = () => {
    markShown();
    setOpen(false);
  };

  const markShown = () => {
    if (!DEV_FORCE && orgId) {
      localStorage.setItem(`${MODAL_KEY_PREFIX}${orgId}`, 'true');
      localStorage.setItem(`${PREV_CANCEL_KEY_PREFIX}${orgId}`, 'false');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          handleStayOnQuarterly();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="inline-flex items-center gap-1.5 mb-3">
            <span className="size-2 rounded-full bg-primary" />
            <span className="text-xs font-semibold text-primary">
              Welcome back
            </span>
          </div>

          <DialogTitle className="text-xl font-bold text-foreground">
            Good to have you back
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">
          Your account has been reactivated. Everything is right where you left
          it — your pipeline, your damage reports, your history.
        </p>

        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-sm text-muted-foreground">
            {isPriceSensitive
              ? "We've noted your feedback. Annual billing gives you the lowest rate — $50/seat/mo — plus 10 free estimates and reports."
              : 'Switch to annual and get 10 free Xactimate estimates + 10 free measurement reports every year — up to $1,090 in value.'}
          </p>
        </div>

        <Button
          className="w-full"
          onClick={handleSwitchToAnnual}
          disabled={switching}
        >
          {switching ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
          Switch to annual — $50/seat/mo
        </Button>

        <div className="space-y-1 text-center">
          <p className="text-xs text-muted-foreground">Stay on quarterly</p>
          <p className="text-xs text-muted-foreground">
            $75/seat/mo, billed quarterly. No free estimates or reports.
          </p>
          <Button
            variant="outline"
            className="w-full"
            onClick={handleStayOnQuarterly}
            disabled={switching}
          >
            Continue on quarterly
          </Button>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          You can change your plan at any time from billing settings.
        </p>
      </DialogContent>
    </Dialog>
  );
}
